import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import SimpleDialog from 'src/components/simple-dialog';
import Iconify from 'src/components/iconify/iconify';
import { EldVehicle } from 'src/models';
import axios, { endpoints } from 'src/utils/axios';
import { DispatcherFleet, DispatcherFleetUser } from '../types';

const DEFAULT_LATE_THRESHOLD_HOURS = 0.5;

type NotificationToggle = {
  field:
    | 'notifyLate'
    | 'notifyOffRoute'
    | 'notifyTripStarted'
    | 'notifyTripFinished'
    | 'notifyDisconnected'
    | 'notifyMissingDocs';
  label: string;
};

const NOTIFICATION_TOGGLES: NotificationToggle[] = [
  { field: 'notifyLate', label: 'Trip running late' },
  { field: 'notifyOffRoute', label: 'Driver off route (15+ miles)' },
  { field: 'notifyTripStarted', label: 'Trip started automatically' },
  { field: 'notifyTripFinished', label: 'Trip finished' },
  { field: 'notifyDisconnected', label: 'Driver disconnected mid-trip' },
  { field: 'notifyMissingDocs', label: 'Driving without shipping docs' },
];

type FormState = {
  eldVehicleIds: number[];
  notifyLate: boolean;
  lateThresholdHours: number;
  notifyOffRoute: boolean;
  notifyTripStarted: boolean;
  notifyTripFinished: boolean;
  notifyDisconnected: boolean;
  notifyMissingDocs: boolean;
};

// notifications default to none: no emails go out unless the dispatcher
// really asked for them
function initialForm(user: DispatcherFleetUser): FormState {
  const { fleet } = user;
  return {
    eldVehicleIds: fleet?.eldVehicleIds || [],
    notifyLate: fleet ? fleet.notifyLate : false,
    lateThresholdHours: fleet?.lateThresholdHours || DEFAULT_LATE_THRESHOLD_HOURS,
    notifyOffRoute: fleet ? fleet.notifyOffRoute : false,
    notifyTripStarted: fleet ? fleet.notifyTripStarted : false,
    notifyTripFinished: fleet ? fleet.notifyTripFinished : false,
    notifyDisconnected: fleet ? fleet.notifyDisconnected : false,
    notifyMissingDocs: fleet ? fleet.notifyMissingDocs : false,
  };
}

type FleetEditDialogProps = {
  user: DispatcherFleetUser;
  eldVehicles: EldVehicle[];
  allFleetUsers: DispatcherFleetUser[];
  onSaved: (updated: DispatcherFleetUser) => void;
  onCancel: VoidFunction;
};

export default function FleetEditDialog({
  user,
  eldVehicles,
  allFleetUsers,
  onSaved,
  onCancel,
}: FleetEditDialogProps) {
  const [form, setForm] = useState<FormState>(() => initialForm(user));
  const [saving, setSaving] = useState(false);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const vehicleOptions = useMemo(
    () => eldVehicles.filter((vehicle) => vehicle.active),
    [eldVehicles]
  );
  const selectedVehicles = useMemo(
    () => vehicleOptions.filter((vehicle) => form.eldVehicleIds.includes(vehicle.id)),
    [vehicleOptions, form.eldVehicleIds]
  );

  // trucks already in another dispatcher's fleet, so double assignments are a decision, not an accident
  const alsoAssigned = useMemo(() => {
    const lines: string[] = [];
    selectedVehicles.forEach((vehicle) => {
      const others = allFleetUsers.filter(
        (fleetUser) =>
          fleetUser.userId !== user.userId && fleetUser.fleet?.eldVehicleIds?.includes(vehicle.id)
      );
      if (others.length > 0) {
        lines.push(`#${vehicle.vehicleUnitNumber} is also in ${others.map((o) => o.name).join(', ')}'s fleet`);
      }
    });
    return lines;
  }, [selectedVehicles, allFleetUsers, user.userId]);

  const save = async () => {
    setSaving(true);
    try {
      const response = await axios.put(endpoints.dispatcherFleets.upsert, {
        userId: user.userId,
        eldVehicleIds: form.eldVehicleIds,
        notifyLate: form.notifyLate,
        lateThresholdHours: form.lateThresholdHours,
        notifyOffRoute: form.notifyOffRoute,
        notifyTripStarted: form.notifyTripStarted,
        notifyTripFinished: form.notifyTripFinished,
        notifyDisconnected: form.notifyDisconnected,
        notifyMissingDocs: form.notifyMissingDocs,
      });
      enqueueSnackbar('Fleet saved');
      onSaved({
        ...user,
        fleet: response.data.fleet as DispatcherFleet,
      });
    } catch (requestError: any) {
      const validations = requestError?.response?.data;
      if (Array.isArray(validations)) {
        validations.forEach((validation: { message: string }) =>
          enqueueSnackbar(validation.message, { variant: 'error' })
        );
      } else {
        enqueueSnackbar('Could not save the fleet', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <SimpleDialog
      title={user.name}
      open
      onCancel={onCancel}
      onSubmit={save}
      submitDisabled={saving}
      submitButton="Save Fleet"
      submitButtonIcon={<Iconify icon="basil:save-solid" />}
      submitButtonColor="info"
    >
      <Stack direction="column" gap={2}>
        <Typography variant="subtitle2">Trucks</Typography>
        <Autocomplete
          multiple
          options={vehicleOptions}
          value={selectedVehicles}
          onChange={(_, vehicles) =>
            setField(
              'eldVehicleIds',
              vehicles.map((vehicle) => vehicle.id)
            )
          }
          getOptionLabel={(vehicle) => vehicle.vehicleUnitNumber || ''}
          renderTags={(vehicles, getTagProps) =>
            vehicles.map((vehicle, index) => (
              <Chip
                {...getTagProps({ index })}
                key={vehicle.id}
                size="small"
                label={`#${vehicle.vehicleUnitNumber}`}
              />
            ))
          }
          renderInput={(params) => <TextField {...params} placeholder="Add trucks" />}
        />
        {alsoAssigned.map((line) => (
          <Typography key={line} variant="caption" color="warning.main">
            {line}
          </Typography>
        ))}

        <Typography variant="subtitle2">Email notifications</Typography>
        <Stack direction="column">
          {NOTIFICATION_TOGGLES.map((toggle) => (
            <Stack key={toggle.field} direction="row" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form[toggle.field]}
                    onChange={(event) => setField(toggle.field, event.target.checked)}
                  />
                }
                label={toggle.label}
              />
              {toggle.field === 'notifyLate' && (
                <TextField
                  size="small"
                  type="number"
                  label="Behind by (hours)"
                  value={form.lateThresholdHours}
                  disabled={!form.notifyLate}
                  onChange={(event) =>
                    setField('lateThresholdHours', parseFloat(event.target.value) || 0)
                  }
                  inputProps={{ min: 0, step: 0.25 }}
                  sx={{ width: 160 }}
                />
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </SimpleDialog>
  );
}
